/**
 * ReSettlementPage Component
 * 재마감 페이지
 */
import React, { useState, useCallback, useEffect } from 'react';
import DateInput from '@/shared/components/forms/DateInput';
import Loading from '@/shared/components/feedback/Loading';
import { useReSettlement } from '../hooks/useReSettlement';
import { getTodayDate } from './reSettlement.helpers';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import '@/shared/styles/search-form.css';
import '../styles/ReSettlementPage.css';

const ReSettlementPage: React.FC = () => {
    // ======== 훅 ========
    const { showToast } = useToast();
    const {
        loading,
        tableOptions,
        tableOptionsLoading,
        applyReSettlement,
        suppliers,
        suppliersLoading,
        riders,
        ridersLoading,
        fetchRiders,
    } = useReSettlement();

    // ======== 상태 ========
    const [selectedTableName, setSelectedTableName] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<string>(getTodayDate);
    const [selectedSuppId, setSelectedSuppId] = useState<string>('');
    const [selectedRiderId, setSelectedRiderId] = useState<string>('');

    // ======== 핸들러 ========

    /** 테이블 선택 변경 핸들러 */
    const handleTableNameChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedTableName(e.target.value);
    }, []);

    /** 날짜 변경 핸들러 */
    const handleDateChange = useCallback((value: string) => {
        setSelectedDate(value);
    }, []);

    /** 협력사 선택 변경 핸들러 */
    const handleSupplierChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const suppId = e.target.value;
        setSelectedSuppId(suppId);
        setSelectedRiderId(''); // 협력사 변경 시 라이더 초기화
    }, []);

    /** 라이더 선택 변경 핸들러 */
    const handleRiderChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedRiderId(e.target.value);
    }, []);

    /** 적용 버튼 클릭 핸들러 */
    const handleApply = useCallback(async () => {
        if (!selectedTableName) {
            showToast('구분을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!selectedDate) {
            return;
        }
        await applyReSettlement(selectedTableName, selectedDate, selectedSuppId, selectedRiderId);
    }, [selectedTableName, selectedDate, selectedSuppId, selectedRiderId, applyReSettlement, showToast]);

    // 협력사 변경 시 라이더 목록 조회
    useEffect(() => {
        fetchRiders(selectedSuppId);
    }, [selectedSuppId, fetchRiders]);

    return (
        <div className="re-settlement-page">
            {/* 로딩 오버레이 */}
            {loading && <Loading message="재마감 처리 중..." fullScreen />}

            <div className="page-header">
                <h1 className="page-title">재마감</h1>
            </div>

            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form re-settlement-search-form">
                        {/* 구분 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-list" />
                                    구분
                                </label>
                                <div className="status-select-wrapper">
                                    <select
                                        className="status-select"
                                        value={selectedTableName}
                                        onChange={handleTableNameChange}
                                        disabled={tableOptionsLoading}
                                    >
                                        <option value="">선택</option>
                                        {tableOptions.map((option) => (
                                            <option key={option.tableName} value={option.tableName}>
                                                {option.descriptionTitle}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* 협력사 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <div className="status-select-wrapper">
                                    <select
                                        className="status-select"
                                        value={selectedSuppId}
                                        onChange={handleSupplierChange}
                                        disabled={suppliersLoading}
                                    >
                                        <option value="">전체</option>
                                        {suppliers.map((supplier) => (
                                            <option key={supplier.suppId} value={supplier.suppId}>
                                                {supplier.suppName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* 라이더 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-motorcycle" />
                                    라이더
                                </label>
                                <div className="status-select-wrapper">
                                    <select
                                        className="status-select"
                                        value={selectedRiderId}
                                        onChange={handleRiderChange}
                                        disabled={ridersLoading || !selectedSuppId}
                                    >
                                        <option value="">전체</option>
                                        {riders.map((rider) => (
                                            <option key={rider.riderId} value={rider.riderId}>
                                                {rider.riderName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* 날짜 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-calendar-alt" />
                                    날짜
                                </label>
                                <DateInput
                                    id="reSettlementDate"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                />
                            </div>
                        </div>

                        {/* 버튼 그룹 */}
                        <div className="form-group button-group">
                            <button
                                className="btn btn-primary"
                                onClick={handleApply}
                                disabled={true}
                            >
                                <i className="fas fa-check" />
                                적용
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReSettlementPage;

